import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { adminTokens as t, type AdminStatusTone } from './adminTokens';
import { adminStatusStyle, adminSurface, adminText } from './adminStyles';
import { AdminIcon, adminIconSize, adminStatusIcons } from './adminIcons';

const NARROW_QUERY = '(max-width: 640px)';

/** True below 640 px, where admin pages switch to the 16 px gutter. */
export function useAdminNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(NARROW_QUERY).matches
      : false
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(NARROW_QUERY);
    const update = () => setNarrow(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return narrow;
}

interface AdminPageProps {
  children: ReactNode;
  /** Caps the readable width for settings-style pages; lists keep the full width. */
  maxWidth?: string;
  style?: CSSProperties;
}

/** Management-page frame: the tinted canvas plus page gutters. */
export function AdminPage({ children, maxWidth, style }: AdminPageProps) {
  const narrow = useAdminNarrowViewport();
  return (
    <div style={{ ...adminSurface.page, ...(narrow ? adminSurface.pageNarrow : {}), ...style }}>
      {maxWidth ? (
        <div style={{ width: '100%', maxWidth, display: 'flex', flexDirection: 'column', gap: t.spacing.lg }}>
          {children}
        </div>
      ) : children}
    </div>
  );
}

interface AdminIconWellProps {
  icon: IconDefinition;
  /** 32 px well for inline emphasis, 40 px tile for section and page markers. */
  size?: 32 | 40;
  tone?: 'neutral' | 'rose' | 'tile';
}

export function AdminIconWell({ icon, size = 32, tone = 'neutral' }: AdminIconWellProps) {
  const toneStyle: CSSProperties = tone === 'rose'
    ? { backgroundColor: t.surface.rose, color: t.brand.roseForeground }
    : tone === 'tile'
      ? { backgroundColor: t.surface.panel, color: t.text.primary, border: `1px solid ${t.border.subtle}` }
      : { backgroundColor: t.surface.subtle, color: t.text.secondary };
  return (
    <span
      aria-hidden="true"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: size === 40 ? t.radius.tile : t.radius.well,
        boxSizing: 'border-box',
        ...toneStyle,
      }}
    >
      <AdminIcon icon={icon} size={size === 40 ? adminIconSize.section : adminIconSize.inline + 1} />
    </span>
  );
}

interface AdminPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** The page's existing primary action(s). Wraps below the title at narrow widths. */
  actions?: ReactNode;
  /** Pages under the shell's h1 use h2 (the default). */
  headingLevel?: 'h1' | 'h2';
  titleId?: string;
}

/**
 * Page introduction: title, optional description and the page's own actions.
 * Management pages only, never inside editor or course surfaces.
 */
export function AdminPageHeader({
  title,
  description,
  actions,
  headingLevel = 'h2',
  titleId,
}: AdminPageHeaderProps) {
  const Heading = headingLevel;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: t.spacing.lg,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.md, minWidth: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <Heading id={titleId} style={adminText.pageTitle}>{title}</Heading>
          {description && <p style={adminText.secondary}>{description}</p>}
        </div>
      </div>
      {actions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.sm, flexWrap: 'wrap' }}>
          {actions}
        </div>
      )}
    </div>
  );
}

interface AdminPanelProps {
  children: ReactNode;
  as?: 'section' | 'div';
  padded?: boolean;
  style?: CSSProperties;
  'aria-labelledby'?: string;
  'aria-label'?: string;
}

/** White work panel: lists, forms, settings groups. */
export function AdminPanel({ children, as = 'section', padded = true, style, ...aria }: AdminPanelProps) {
  const Element = as;
  return (
    <Element style={{ ...adminSurface.panel, ...(padded ? adminSurface.panelPadded : {}), ...style }} {...aria}>
      {children}
    </Element>
  );
}

interface AdminSectionHeadingProps {
  title: ReactNode;
  icon?: IconDefinition;
  description?: ReactNode;
  actions?: ReactNode;
  headingLevel?: 'h2' | 'h3' | 'h4';
  titleId?: string;
}

/** Section title inside a panel, with an optional icon well. The text alone identifies the section. */
export function AdminSectionHeading({
  title,
  icon,
  description,
  actions,
  headingLevel = 'h3',
  titleId,
}: AdminSectionHeadingProps) {
  const Heading = headingLevel;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: t.spacing.md, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.md, minWidth: 0 }}>
        {icon && <AdminIconWell icon={icon} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <Heading id={titleId} style={adminText.sectionTitle}>{title}</Heading>
          {description && <p style={{ ...adminText.secondary, fontSize: t.typography.label.size }}>{description}</p>}
        </div>
      </div>
      {actions && <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.sm, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

interface AdminStatusMessageProps {
  tone: AdminStatusTone;
  children: ReactNode;
  /** Defaults to `alert` for errors and `status` otherwise; pass `null` for static text. */
  role?: 'alert' | 'status' | null;
  style?: CSSProperties;
}

/** Inline status message: the text states the outcome; tone and icon only support it. */
export function AdminStatusMessage({ tone, children, role, style }: AdminStatusMessageProps) {
  const resolvedRole = role === undefined ? (tone === 'error' ? 'alert' : 'status') : role ?? undefined;
  return (
    <div role={resolvedRole} style={{ ...adminStatusStyle(tone), ...style }}>
      <AdminIcon icon={adminStatusIcons[tone]} size={adminIconSize.inline} style={{ marginTop: '3px' }} />
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}
